/**
 * Skipp search intelligence engine.
 *
 * Storefront ranking/query layer on top of Shopify search.
 * Modules: normalize, fuzzy, correct, synonyms, intent, retrieve-query, rank, cache, analytics.
 */
(function (root, factory) {
  'use strict';
  var api = factory(root.SKIPP_SEARCH_CONFIG || {});
  root.SkippSearch = api;
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function (rawConfig) {
  'use strict';

  function assign(target) {
    for (var i = 1; i < arguments.length; i++) {
      var src = arguments[i];
      if (!src) continue;
      Object.keys(src).forEach(function (key) {
        target[key] = src[key];
      });
    }
    return target;
  }

  var DEFAULTS = rootDefaults();
  var config = assign({}, DEFAULTS, rawConfig || {});

  function rootDefaults() {
    return {
      SEARCH_DEBOUNCE_MS: 300,
      CACHE_TTL_MS: 300000,
      CACHE_MAX_ENTRIES: 80,
      ANALYTICS_MAX_EVENTS: 200,
      LEARNED_TYPO_MIN_CLICKS: 1,
      TYPO_DISTANCE_THRESHOLD: 2,
      TYPO_MIN_TOKEN_LENGTH: 4,
      PREFIX_MIN_TOKEN_LENGTH: 3,
      JARO_WINKLER_MIN: 0.89,
      CORRECTION_CONFIDENCE_GAP: 0.04,
      COMPOUND_MIN_LENGTH: 8,
      MIN_RELEVANCE_FOR_BOOST: 28,
      MIN_RELEVANCE_TO_KEEP: 12,
      STRONG_MATCH_THRESHOLD: 48,
      SIMILARITY_MIN: 0.58,
      SIMILARITY_STRONG: 0.78,
      SIMILARITY_MAX_MATCHES: 6,
      TITLE_MATCH_WEIGHT: 100,
      BRAND_MATCH_WEIGHT: 86,
      CATEGORY_MATCH_WEIGHT: 74,
      PRODUCT_TYPE_MATCH_WEIGHT: 70,
      COLLECTION_MATCH_WEIGHT: 62,
      ATTRIBUTE_MATCH_WEIGHT: 48,
      COLOR_MATCH_WEIGHT: 42,
      GENDER_MATCH_WEIGHT: 38,
      STYLE_MATCH_WEIGHT: 34,
      TAG_MATCH_WEIGHT: 26,
      SYNONYM_MATCH_WEIGHT: 22,
      PARTIAL_MATCH_WEIGHT: 16,
      FUZZY_MATCH_WEIGHT: 8,
      BOOST_WEIGHT: 18,
      AVAILABILITY_WEIGHT: 6,
      SHOPIFY_POSITION_WEIGHT: 4,
      GENDER_MISMATCH_PENALTY: 55,
      BRAND_MISMATCH_PENALTY: 48,
      COLOR_MISMATCH_PENALTY: 32,
      CATEGORY_MISMATCH_PENALTY: 40,
      PREDICTIVE_MAX_PRODUCTS: 8,
      STOPWORDS: ['for', 'the', 'a', 'an', 'of', 'with', 'and', 'in', 'on', 'to', 'from', 'by', 'or'],
      ABBREVIATIONS: {},
      COMPOUNDS: {},
      SYNONYMS: {},
      GENDERS: {},
      COLORS: [],
      CATEGORIES: [],
      CATEGORY_FAMILIES: {},
      STYLES: [],
      SIZES: [],
      MATERIALS: [],
      OCCASIONS: [],
      BRANDS: [],
      COMMON_SEARCH_TERMS: [],
    };
  }

  /* ------------------------------------------------------------------ */
  /* Catalog index                                                       */
  /* ------------------------------------------------------------------ */

  var catalog = {
    brands: [],
    collections: [],
    types: [],
    tags: [],
    titles: [],
    extra: [],
  };

  var indexes = {
    exact: Object.create(null),
    byPrefix2: Object.create(null),
    synonymToCanonical: Object.create(null),
    canonicalToSynonyms: Object.create(null),
    compoundKeys: [],
    reverseCompounds: Object.create(null),
    genderLookup: Object.create(null),
    colorSet: Object.create(null),
    categorySet: Object.create(null),
    styleSet: Object.create(null),
    sizeSet: Object.create(null),
    materialSet: Object.create(null),
    occasionSet: Object.create(null),
    brandSet: Object.create(null),
    familyOf: Object.create(null),
    stopSet: Object.create(null),
    compactPhrases: [],
    built: false,
  };

  function addCompactPhrase(phrase, kind) {
    var normalized = normalizeToken(phrase);
    if (!normalized) return;
    var compact = normalized.replace(/\s+/g, '');
    if (compact.length < 6) return;
    indexes.compactPhrases = indexes.compactPhrases || [];
    indexes.compactPhrases.push({ compact: compact, phrase: normalized, kind: kind || 'catalog' });
  }

  function addTerm(term, kind, extraWeight) {
    var normalized = normalizeToken(term);
    if (!normalized || normalized.length < 2) return;
    var existing = indexes.exact[normalized];
    var weight = extraWeight || 1;
    if (!existing || weight > existing.weight) {
      indexes.exact[normalized] = { term: normalized, kind: kind, weight: weight };
    }
    var prefix = normalized.slice(0, 2);
    if (!indexes.byPrefix2[prefix]) indexes.byPrefix2[prefix] = [];
    if (indexes.byPrefix2[prefix].indexOf(normalized) === -1) {
      indexes.byPrefix2[prefix].push(normalized);
    }
    if (normalized.indexOf(' ') !== -1 || normalized.length >= 6) {
      addCompactPhrase(normalized, kind);
    }
  }

  function addList(list, kind, weight) {
    (list || []).forEach(function (item) {
      addTerm(item, kind, weight);
    });
  }

  function rebuildIndexes() {
    indexes.exact = Object.create(null);
    indexes.byPrefix2 = Object.create(null);
    indexes.synonymToCanonical = Object.create(null);
    indexes.canonicalToSynonyms = Object.create(null);
    indexes.compoundKeys = [];
    indexes.reverseCompounds = Object.create(null);
    indexes.genderLookup = Object.create(null);
    indexes.colorSet = Object.create(null);
    indexes.categorySet = Object.create(null);
    indexes.styleSet = Object.create(null);
    indexes.sizeSet = Object.create(null);
    indexes.materialSet = Object.create(null);
    indexes.occasionSet = Object.create(null);
    indexes.brandSet = Object.create(null);
    indexes.familyOf = Object.create(null);
    indexes.stopSet = Object.create(null);
    indexes.compactPhrases = [];

    (config.STOPWORDS || []).forEach(function (w) {
      indexes.stopSet[w] = true;
    });

    Object.keys(config.GENDERS || {}).forEach(function (gender) {
      (config.GENDERS[gender] || []).forEach(function (alias) {
        indexes.genderLookup[normalizeToken(alias)] = gender;
        addTerm(alias, 'gender', 8);
      });
      addTerm(gender, 'gender', 8);
    });

    (config.COLORS || []).forEach(function (c) {
      var n = normalizeToken(c);
      indexes.colorSet[n] = n;
      addTerm(c, 'color', 7);
    });
    (config.CATEGORIES || []).forEach(function (c) {
      var n = normalizeToken(c);
      indexes.categorySet[n] = n;
      addTerm(c, 'category', 9);
    });
    (config.STYLES || []).forEach(function (c) {
      indexes.styleSet[normalizeToken(c)] = true;
      addTerm(c, 'style', 6);
    });
    (config.SIZES || []).forEach(function (c) {
      indexes.sizeSet[normalizeToken(c)] = true;
      addTerm(c, 'size', 4);
    });
    (config.MATERIALS || []).forEach(function (c) {
      indexes.materialSet[normalizeToken(c)] = true;
      addTerm(c, 'material', 5);
    });
    (config.OCCASIONS || []).forEach(function (c) {
      indexes.occasionSet[normalizeToken(c)] = true;
      addTerm(c, 'occasion', 5);
    });
    (config.BRANDS || []).forEach(function (c) {
      var n = normalizeToken(c).replace(/\s+/g, '');
      var spaced = normalizeToken(c);
      indexes.brandSet[spaced] = spaced;
      if (n) indexes.brandSet[n] = spaced;
      addTerm(c, 'brand', 10);
    });

    Object.keys(config.CATEGORY_FAMILIES || {}).forEach(function (cat) {
      indexes.familyOf[normalizeToken(cat)] = config.CATEGORY_FAMILIES[cat];
    });

    Object.keys(config.SYNONYMS || {}).forEach(function (canonical) {
      var can = normalizeToken(canonical);
      var aliases = config.SYNONYMS[canonical] || [];
      indexes.canonicalToSynonyms[can] = aliases.map(normalizeToken);
      indexes.synonymToCanonical[can] = can;
      addTerm(can, indexes.categorySet[can] ? 'category' : 'synonym', 9);
      aliases.forEach(function (alias) {
        var a = normalizeToken(alias);
        if (!a) return;
        indexes.synonymToCanonical[a] = can;
        addTerm(a, 'synonym', 8);
      });
    });

    Object.keys(config.COMPOUNDS || {}).forEach(function (phrase) {
      var from = normalizeToken(phrase);
      var to = normalizeToken(config.COMPOUNDS[phrase]);
      if (!from || !to) return;
      indexes.compoundKeys.push(from);
      indexes.reverseCompounds[to] = indexes.reverseCompounds[to] || [];
      if (from.indexOf(' ') !== -1) indexes.reverseCompounds[to].push(from);
      addTerm(from, 'compound', 7);
      addTerm(to, 'compound', 7);
    });
    indexes.compoundKeys.sort(function (a, b) {
      return b.length - a.length;
    });

    addList(config.COMMON_SEARCH_TERMS, 'common', 5);
    addList(catalog.brands, 'brand', 11);
    addList(catalog.collections, 'collection', 8);
    addList(catalog.types, 'type', 8);
    addList(catalog.tags, 'tag', 5);
    addList(catalog.titles, 'title', 6);
    addList(catalog.extra, 'catalog', 6);

    (catalog.brands || []).forEach(function (b) {
      var spaced = normalizeToken(b);
      if (spaced) indexes.brandSet[spaced] = spaced;
    });

    indexes.built = true;
    memoryCache = Object.create(null);
  }

  function setCatalog(next) {
    catalog = {
      brands: uniqueStrings((next && next.brands) || []),
      collections: uniqueStrings((next && next.collections) || []),
      types: uniqueStrings((next && next.types) || []),
      tags: uniqueStrings((next && next.tags) || []),
      titles: uniqueStrings((next && next.titles) || []),
      extra: uniqueStrings((next && next.extra) || []),
    };
    rebuildIndexes();
    return catalog;
  }

  function mergeCatalog(next) {
    return setCatalog({
      brands: catalog.brands.concat((next && next.brands) || []),
      collections: catalog.collections.concat((next && next.collections) || []),
      types: catalog.types.concat((next && next.types) || []),
      tags: catalog.tags.concat((next && next.tags) || []),
      titles: catalog.titles.concat((next && next.titles) || []),
      extra: catalog.extra.concat((next && next.extra) || []),
    });
  }

  function uniqueStrings(list) {
    var seen = Object.create(null);
    var out = [];
    (list || []).forEach(function (item) {
      var n = String(item || '').trim();
      if (!n) return;
      var key = n.toLowerCase();
      if (seen[key]) return;
      seen[key] = true;
      out.push(n);
    });
    return out;
  }

  function escapeRegExp(value) {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  rebuildIndexes();

  /* ------------------------------------------------------------------ */
  /* Normalization                                                       */
  /* ------------------------------------------------------------------ */

  function normalizeToken(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[’']/g, '')
      .replace(/&/g, ' and ')
      .replace(/[_/]+/g, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function applyCompounds(text) {
    var out = ' ' + text + ' ';
    indexes.compoundKeys.forEach(function (phrase) {
      if (phrase.indexOf(' ') === -1) return;
      var replacement = normalizeToken(config.COMPOUNDS[phrase] || phrase);
      out = out.replace(new RegExp('\\s' + escapeRegExp(phrase) + '\\s', 'g'), ' ' + replacement + ' ');
    });
    return out.trim().replace(/\s+/g, ' ');
  }

  function applyAbbreviations(tokens) {
    return tokens.map(function (tok) {
      var mapped = config.ABBREVIATIONS[tok];
      return mapped ? normalizeToken(mapped) : tok;
    });
  }

  function lightStem(token) {
    if (!token || token.length < 4) return token;
    if (token === 'men' || token === 'women' || token === 'kids') return token;
    if (indexes.brandSet[token] || indexes.colorSet[token]) return token;
    if (token.endsWith('ies') && token.length > 5) return token.slice(0, -3) + 'y';
    if (token.endsWith('sses')) return token.slice(0, -2);
    if (token.endsWith('ches') || token.endsWith('shes') || token.endsWith('xes')) return token.slice(0, -2);
    if (token.endsWith('ses') && token.length > 5) return token.slice(0, -2);
    if (token.endsWith('s') && !token.endsWith('ss') && token.length > 4) return token.slice(0, -1);
    return token;
  }

  function isStrongTerm(token) {
    if (!token || token.length < 3) return false;
    var meta = indexes.exact[token];
    var kind = meta && meta.kind;
    return !!(
      indexes.synonymToCanonical[token] ||
      indexes.brandSet[token] ||
      indexes.categorySet[token] ||
      kind === 'brand' ||
      kind === 'category' ||
      kind === 'collection' ||
      kind === 'type'
    );
  }

  function resolveCompactPhrase(token) {
    if (!token || token.length < 6) return null;
    var compact = String(token).replace(/\s+/g, '');
    var phrases = indexes.compactPhrases || [];
    var i;
    for (i = 0; i < phrases.length; i++) {
      if (phrases[i].compact === compact && phrases[i].phrase !== compact) {
        return phrases[i].phrase;
      }
    }
    var best = null;
    for (i = 0; i < phrases.length; i++) {
      var item = phrases[i];
      if (Math.abs(item.compact.length - compact.length) > 2) continue;
      var dist = damerauLevenshtein(compact, item.compact);
      if (dist === 0) return item.phrase;
      if (dist > 1) continue;
      var jw = jaroWinkler(compact, item.compact);
      if (jw < 0.94) continue;
      if (!best || jw > best.jw) best = { phrase: item.phrase, jw: jw };
    }
    return best ? best.phrase : null;
  }

  function splitConcatenated(token) {
    if (!token || token.length < config.COMPOUND_MIN_LENGTH) return [token];
    if (indexes.exact[token] || indexes.synonymToCanonical[token] || indexes.brandSet[token]) {
      return [token];
    }
    var compactPhrase = resolveCompactPhrase(token);
    if (compactPhrase && compactPhrase.indexOf(' ') !== -1) {
      return compactPhrase.split(/\s+/).filter(Boolean);
    }
    var best = null;
    var i;
    for (i = 3; i <= token.length - 3; i++) {
      var left = token.slice(0, i);
      var right = token.slice(i);
      var bothKnown = isStrongTerm(left) && isStrongTerm(right);
      var suffixKnown = right.length >= 4 && isStrongTerm(right) && left.length >= 3;
      if (!bothKnown && !suffixKnown) continue;
      var score = (bothKnown ? 100 : 0) + right.length;
      if (!best || score > best.score) best = { parts: [left, right], score: score };
    }
    return best ? best.parts : [token];
  }

  function normalizeQuery(query) {
    var raw = String(query || '');
    var collapsed = raw
      .toLowerCase()
      .replace(/[’']/g, '')
      .replace(/&/g, ' and ')
      .replace(/[-_]/g, ' ')
      .replace(/[^a-z0-9\s]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
    var compounded = applyCompounds(collapsed);
    var tokens = compounded.split(/\s+/).filter(Boolean);
    tokens = applyAbbreviations(tokens);
    var expanded = [];
    tokens.forEach(function (tok) {
      splitConcatenated(tok).forEach(function (part) {
        expanded.push(part);
      });
    });
    var mapped = expanded.map(function (tok) {
      if (indexes.stopSet[tok]) return tok;
      return indexes.synonymToCanonical[tok] || tok;
    });
    return {
      original: raw,
      normalized: mapped.join(' ').replace(/\s+/g, ' ').trim(),
      tokens: mapped.filter(Boolean),
    };
  }

  function tokenize(query) {
    return normalizeQuery(query).tokens;
  }

  /* ------------------------------------------------------------------ */
  /* Fuzzy matching                                                      */
  /* ------------------------------------------------------------------ */

  function damerauLevenshtein(a, b) {
    a = String(a || '');
    b = String(b || '');
    var m = a.length;
    var n = b.length;
    if (a === b) return 0;
    if (!m) return n;
    if (!n) return m;
    var inf = m + n;
    var da = Object.create(null);
    var H = [];
    var i;
    var j;
    for (i = 0; i <= m + 1; i++) {
      H[i] = [];
      for (j = 0; j <= n + 1; j++) H[i][j] = 0;
    }
    H[0][0] = inf;
    for (i = 0; i <= m; i++) {
      H[i + 1][0] = inf;
      H[i + 1][1] = i;
    }
    for (j = 0; j <= n; j++) {
      H[0][j + 1] = inf;
      H[1][j + 1] = j;
    }
    for (i = 1; i <= m; i++) {
      var db = 0;
      for (j = 1; j <= n; j++) {
        var i1 = da[b.charAt(j - 1)] || 0;
        var j1 = db;
        var cost = 1;
        if (a.charAt(i - 1) === b.charAt(j - 1)) {
          cost = 0;
          db = j;
        }
        H[i + 1][j + 1] = Math.min(
          H[i][j] + cost,
          H[i + 1][j] + 1,
          H[i][j + 1] + 1,
          H[i1][j1] + (i - i1 - 1) + 1 + (j - j1 - 1)
        );
      }
      da[a.charAt(i - 1)] = i;
    }
    return H[m + 1][n + 1];
  }

  function jaroWinkler(a, b) {
    a = String(a || '');
    b = String(b || '');
    if (a === b) return 1;
    var al = a.length;
    var bl = b.length;
    if (!al || !bl) return 0;
    var matchDistance = Math.max(0, Math.floor(Math.max(al, bl) / 2) - 1);
    var aMatches = [];
    var bMatches = [];
    var matches = 0;
    var transpositions = 0;
    var i;
    var j;
    for (i = 0; i < al; i++) {
      var start = Math.max(0, i - matchDistance);
      var end = Math.min(i + matchDistance + 1, bl);
      for (j = start; j < end; j++) {
        if (bMatches[j] || a.charAt(i) !== b.charAt(j)) continue;
        aMatches[i] = true;
        bMatches[j] = true;
        matches++;
        break;
      }
    }
    if (!matches) return 0;
    var k = 0;
    for (i = 0; i < al; i++) {
      if (!aMatches[i]) continue;
      while (!bMatches[k]) k++;
      if (a.charAt(i) !== b.charAt(k)) transpositions++;
      k++;
    }
    var jaro =
      (matches / al + matches / bl + (matches - transpositions / 2) / matches) / 3;
    var prefix = 0;
    for (i = 0; i < Math.min(4, al, bl); i++) {
      if (a.charAt(i) === b.charAt(i)) prefix++;
      else break;
    }
    return jaro + prefix * 0.1 * (1 - jaro);
  }

  function maxDistanceFor(token) {
    var len = token.length;
    if (len < config.TYPO_MIN_TOKEN_LENGTH) return 0;
    if (len <= 5) return 1;
    return Math.min(config.TYPO_DISTANCE_THRESHOLD, 2);
  }

  /* ------------------------------------------------------------------ */
  /* Nearest brand / product-name similarity                             */
  /* Blend of Jaro-Winkler, normalized edit distance, n-gram Dice,       */
  /* substring/prefix bonuses, and token overlap. Brands rank first.     */
  /* ------------------------------------------------------------------ */

  function ngrams(text, size) {
    var s = String(text || '');
    var n = size || 3;
    if (s.length < n) return s ? [s] : [];
    var grams = [];
    var i;
    for (i = 0; i <= s.length - n; i++) grams.push(s.slice(i, i + n));
    return grams;
  }

  function diceCoefficient(a, b, size) {
    var left = ngrams(a, size);
    var right = ngrams(b, size);
    if (!left.length || !right.length) return 0;
    var counts = Object.create(null);
    left.forEach(function (gram) {
      counts[gram] = (counts[gram] || 0) + 1;
    });
    var overlap = 0;
    right.forEach(function (gram) {
      if (!counts[gram]) return;
      overlap++;
      counts[gram]--;
    });
    return (2 * overlap) / (left.length + right.length);
  }

  function similarityKindBoost(kind) {
    if (kind === 'brand') return 0.06;
    if (kind === 'title') return 0.04;
    if (kind === 'collection') return 0.035;
    if (kind === 'category' || kind === 'type') return 0.03;
    return 0.01;
  }

  function similarityScore(query, candidate, kind) {
    var q = normalizeToken(query);
    var c = normalizeToken(candidate);
    if (!q || !c) return 0;
    var qCompact = q.replace(/\s+/g, '');
    var cCompact = c.replace(/\s+/g, '');
    if (q === c || qCompact === cCompact) return 1;

    var prefixBonus = 0;
    if (c.indexOf(q) === 0 || q.indexOf(c) === 0 || cCompact.indexOf(qCompact) === 0 || qCompact.indexOf(cCompact) === 0) {
      prefixBonus = 0.14;
    } else if (c.indexOf(q) !== -1 || q.indexOf(c) !== -1 || cCompact.indexOf(qCompact) !== -1) {
      prefixBonus = 0.07;
    }

    var jw = Math.max(jaroWinkler(q, c), jaroWinkler(qCompact, cCompact));
    var maxLen = Math.max(qCompact.length, cCompact.length) || 1;
    var levSim = 1 - damerauLevenshtein(qCompact, cCompact) / maxLen;
    if (levSim < 0) levSim = 0;
    var trigram = diceCoefficient(qCompact, cCompact, 3);
    var bigram = diceCoefficient(qCompact, cCompact, 2);

    var qTokens = q.split(/\s+/).filter(Boolean);
    var cTokens = c.split(/\s+/).filter(Boolean);
    var tokenOverlap = 0;
    if (qTokens.length && cTokens.length) {
      var hits = 0;
      qTokens.forEach(function (tok) {
        var best = 0;
        cTokens.forEach(function (ct) {
          if (tok === ct) {
            best = 1;
            return;
          }
          var local = Math.max(jaroWinkler(tok, ct), ct.indexOf(tok) === 0 || tok.indexOf(ct) === 0 ? 0.9 : 0);
          if (local > best) best = local;
        });
        if (best >= 0.86) hits++;
      });
      tokenOverlap = hits / Math.max(qTokens.length, cTokens.length);
    }

    var score =
      0.34 * jw +
      0.28 * levSim +
      0.18 * trigram +
      0.1 * bigram +
      0.1 * tokenOverlap +
      prefixBonus +
      similarityKindBoost(kind);
    return score > 1 ? 1 : score;
  }

  function catalogSimilarityCandidates() {
    var out = [];
    var seen = Object.create(null);
    function add(term, kind) {
      var display = String(term || '').trim();
      var n = normalizeToken(display);
      if (!n || n.length < 2 || indexes.stopSet[n]) return;
      var key = kind + ':' + n;
      if (seen[key]) return;
      seen[key] = true;
      out.push({ term: n, display: display, kind: kind });
    }
    (catalog.brands || []).forEach(function (item) {
      add(item, 'brand');
    });
    (config.BRANDS || []).forEach(function (item) {
      add(item, 'brand');
    });
    Object.keys(indexes.brandSet).forEach(function (key) {
      add(indexes.brandSet[key] || key, 'brand');
    });
    (catalog.titles || []).forEach(function (item) {
      add(item, 'title');
    });
    (catalog.collections || []).forEach(function (item) {
      add(item, 'collection');
    });
    (catalog.types || []).forEach(function (item) {
      add(item, 'type');
    });
    Object.keys(indexes.categorySet).forEach(function (key) {
      add(key, 'category');
    });
    (catalog.extra || []).forEach(function (item) {
      add(item, 'brand');
    });
    (config.COMMON_SEARCH_TERMS || []).forEach(function (item) {
      add(item, 'title');
    });
    return out;
  }

  function nearestMatches(query, options) {
    options = options || {};
    var q = normalizeToken(query);
    if (!q) return [];
    var minScore = options.minScore != null ? options.minScore : config.SIMILARITY_MIN || 0.58;
    var limit = options.limit != null ? options.limit : config.SIMILARITY_MAX_MATCHES || 6;
    var kinds = options.kinds;
    var tokens = q.split(/\s+/).filter(function (tok) {
      return tok && !indexes.stopSet[tok];
    });
    if (!tokens.length) tokens = [q];

    var scored = [];
    catalogSimilarityCandidates().forEach(function (candidate) {
      if (kinds && kinds.indexOf(candidate.kind) === -1) return;
      var phraseScore = similarityScore(q, candidate.term, candidate.kind);
      var tokenScore = 0;
      tokens.forEach(function (tok) {
        var local = similarityScore(tok, candidate.term, candidate.kind);
        if (local > tokenScore) tokenScore = local;
      });
      var score = phraseScore > tokenScore ? phraseScore : tokenScore;
      if (score < minScore) return;
      scored.push({
        term: candidate.display,
        normalized: candidate.term,
        kind: candidate.kind,
        score: score,
      });
    });

    scored.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      var kindOrder = { brand: 0, title: 1, collection: 2, category: 3, type: 4 };
      var ka = kindOrder[a.kind] != null ? kindOrder[a.kind] : 9;
      var kb = kindOrder[b.kind] != null ? kindOrder[b.kind] : 9;
      if (ka !== kb) return ka - kb;
      return a.normalized.length - b.normalized.length;
    });

    var unique = [];
    var seen = Object.create(null);
    scored.forEach(function (item) {
      if (seen[item.normalized]) return;
      seen[item.normalized] = true;
      unique.push(item);
    });
    return unique.slice(0, limit);
  }

  function canRetrieveWithMatch(match) {
    if (!match || !match.term) return false;
    var minStrong = config.SIMILARITY_STRONG || 0.78;
    if (match.score < minStrong) return false;
    if (match.kind === 'brand' || match.kind === 'title' || match.kind === 'catalog') return true;
    if (match.kind === 'category' || match.kind === 'type') return true;
    if (match.kind === 'collection') return match.score >= 0.86;
    return false;
  }

  function firstRetrievableMatch(matches) {
    var i;
    for (i = 0; i < (matches || []).length; i++) {
      if (canRetrieveWithMatch(matches[i])) return matches[i];
    }
    return null;
  }

  function closeBrandMatch(query) {
    var q = normalizeToken(query).replace(/\s+/g, '');
    if (q.length < 4) return null;
    var best = null;
    catalogSimilarityCandidates().forEach(function (candidate) {
      if (
        candidate.kind !== 'brand' &&
        candidate.kind !== 'collection' &&
        candidate.kind !== 'title' &&
        candidate.kind !== 'catalog'
      ) {
        return;
      }
      var compact = candidate.term.replace(/\s+/g, '');
      if (compact.length < 4) return;
      var dist = damerauLevenshtein(q, compact);
      var lenDiff = Math.abs(compact.length - q.length);
      var isPrefix = compact.indexOf(q) === 0 || q.indexOf(compact) === 0;
      var prefixLen = 0;
      var i;
      for (i = 0; i < Math.min(q.length, compact.length); i++) {
        if (q.charAt(i) !== compact.charAt(i)) break;
        prefixLen++;
      }
      var close = (isPrefix && lenDiff <= 3) || (prefixLen >= 4 && dist > 0 && dist <= 2);
      if (!close) return;
      var score = isPrefix ? 0.96 - lenDiff * 0.015 : 0.9 - dist * 0.04;
      if (score > 1) score = 1;
      if (!best || score > best.score) {
        best = {
          term: candidate.display,
          normalized: candidate.term,
          kind: candidate.kind === 'collection' || candidate.kind === 'catalog' ? 'brand' : candidate.kind,
          score: score,
        };
      }
    });
    return best;
  }

  function similarQueryFor(query) {
    var q = normalizeToken(query);
    if (!q) return '';
    var tokens = q.split(/\s+/).filter(function (tok) {
      return tok && !indexes.stopSet[tok];
    });
    if (!tokens.length) return '';
    var minStrong = config.SIMILARITY_STRONG || 0.78;

    if (tokens.length === 1) {
      var close = closeBrandMatch(tokens[0]);
      if (close && close.normalized !== tokens[0]) return close.term;
      var single = firstRetrievableMatch(nearestMatches(tokens[0], { limit: 5, minScore: minStrong }));
      if (!single || single.normalized === tokens[0]) return '';
      return single.term;
    }

    var mapped = tokens.map(function (tok) {
      var localClose = closeBrandMatch(tok);
      if (localClose) return localClose.term;
      var hit = firstRetrievableMatch(nearestMatches(tok, { limit: 5, minScore: minStrong }));
      return hit ? hit.term : tok;
    });
    var joined = uniqueQueries(mapped).join(' ').trim();
    if (!joined || normalizeToken(joined) === q) return '';
    return joined;
  }

  function productNameSimilarity(product, parsed) {
    product = product || {};
    parsed = parsed || process('');
    var original = parsed.original || parsed.normalized || parsed.shopifyQuery || '';
    var titleSim = similarityScore(original, product.title || '', 'title');
    var brandSim = similarityScore(original, product.vendor || '', 'brand');
    if (parsed.similarQuery) {
      titleSim = Math.max(titleSim, similarityScore(parsed.similarQuery, product.title || '', 'title'));
      brandSim = Math.max(brandSim, similarityScore(parsed.similarQuery, product.vendor || '', 'brand'));
    }
    (parsed.tokens || []).forEach(function (tok) {
      if (!tok || indexes.stopSet[tok]) return;
      titleSim = Math.max(titleSim, similarityScore(tok, product.title || '', 'title'));
      brandSim = Math.max(brandSim, similarityScore(tok, product.vendor || '', 'brand'));
    });
    return {
      title: titleSim,
      brand: brandSim,
      best: titleSim > brandSim ? titleSim : brandSim,
    };
  }

  /* ------------------------------------------------------------------ */
  /* Catalog-aware correction                                            */
  /* ------------------------------------------------------------------ */

  var learnedTypos = Object.create(null);

  function setLearnedTypos(map) {
    learnedTypos = map || Object.create(null);
  }

  function candidatePool(token) {
    var prefix = token.slice(0, 2);
    var pool = (indexes.byPrefix2[prefix] || []).slice();
    var first = token.charAt(0);
    Object.keys(indexes.byPrefix2).forEach(function (key) {
      if (key.charAt(0) === first && key !== prefix) {
        pool = pool.concat(indexes.byPrefix2[key]);
      }
    });
    return pool;
  }

  function kindBonus(kind) {
    if (kind === 'brand') return 0.08;
    if (kind === 'category') return 0.06;
    if (kind === 'color' || kind === 'gender') return 0.04;
    if (kind === 'synonym') return 0.03;
    return 0;
  }

  function prefixCorrection(token) {
    if (!token || token.length < config.PREFIX_MIN_TOKEN_LENGTH) return null;
    var prefix = token.slice(0, 2);
    var bucket = indexes.byPrefix2[prefix] || [];
    var matches = [];
    bucket.forEach(function (term) {
      if (term === token) return;
      if (term.indexOf(token) !== 0) return;
      var meta = indexes.exact[term];
      if (!meta) return;
      if (meta.kind === 'brand' || meta.kind === 'category' || meta.kind === 'collection' || meta.kind === 'synonym' || meta.kind === 'color' || meta.kind === 'style') {
        matches.push({ term: term, kind: meta.kind, weight: meta.weight, extra: term.length - token.length });
      }
    });
    if (!matches.length) return null;
    var byCanonical = Object.create(null);
    matches.forEach(function (m) {
      var can = indexes.synonymToCanonical[m.term] || m.term;
      var kind = (indexes.exact[can] && indexes.exact[can].kind) || m.kind;
      if (!byCanonical[can] || m.extra < byCanonical[can].extra) {
        byCanonical[can] = { term: can, kind: kind, weight: m.weight, extra: m.extra };
      }
    });
    matches = Object.keys(byCanonical).map(function (key) {
      return byCanonical[key];
    });
    matches.sort(function (a, b) {
      var kindOrder = { brand: 0, category: 1, collection: 2, synonym: 3, color: 4, style: 5 };
      var ka = kindOrder[a.kind] != null ? kindOrder[a.kind] : 9;
      var kb = kindOrder[b.kind] != null ? kindOrder[b.kind] : 9;
      if (ka !== kb) return ka - kb;
      if (a.extra !== b.extra) return a.extra - b.extra;
      return b.weight - a.weight;
    });
    if (matches.length > 1 && matches[0].kind === matches[1].kind && matches[0].extra === matches[1].extra) {
      return null;
    }
    if (token.length < 4 && matches.length > 1) return null;
    return { term: matches[0].term, method: 'prefix', confidence: 0.84 };
  }

  function fuzzyCorrection(token) {
    var maxDist = maxDistanceFor(token);
    if (!maxDist) return null;
    var pool = candidatePool(token);
    var scored = [];
    var seenCanonical = Object.create(null);
    pool.forEach(function (term) {
      if (Math.abs(term.length - token.length) > maxDist) return;
      var dist = damerauLevenshtein(token, term);
      if (dist === 0 || dist > maxDist) return;
      var jw = jaroWinkler(token, term);
      if (jw < config.JARO_WINKLER_MIN) return;
      var canonical = indexes.synonymToCanonical[term] || term;
      var meta = indexes.exact[canonical] || indexes.exact[term] || { kind: 'catalog', weight: 1 };
      var score = jw + kindBonus(meta.kind) + meta.weight * 0.005 - dist * 0.04;
      if (seenCanonical[canonical] && seenCanonical[canonical].score >= score) return;
      seenCanonical[canonical] = { term: canonical, kind: meta.kind, dist: dist, jw: jw, score: score };
    });
    scored = Object.keys(seenCanonical).map(function (key) {
      return seenCanonical[key];
    });
    if (!scored.length) return null;
    scored.sort(function (a, b) {
      return b.score - a.score;
    });
    if (scored.length > 1 && scored[0].score - scored[1].score < config.CORRECTION_CONFIDENCE_GAP) {
      return null;
    }
    return {
      term: scored[0].term,
      method: 'fuzzy',
      confidence: scored[0].jw,
      dist: scored[0].dist,
    };
  }

  function correctToken(token) {
    if (!token) return { term: token, method: 'empty' };
    if (indexes.stopSet[token]) return { term: token, method: 'stopword' };
    if (learnedTypos[token]) {
      return { term: learnedTypos[token], method: 'learned', confidence: 0.95 };
    }
    if (indexes.exact[token]) {
      var canonicalExact = indexes.synonymToCanonical[token] || token;
      return { term: canonicalExact, method: 'exact', confidence: 1 };
    }
    if (indexes.synonymToCanonical[token]) {
      return { term: indexes.synonymToCanonical[token], method: 'synonym', confidence: 0.98 };
    }
    var stemmed = lightStem(token);
    if (stemmed !== token && (indexes.exact[stemmed] || indexes.synonymToCanonical[stemmed])) {
      return {
        term: indexes.synonymToCanonical[stemmed] || stemmed,
        method: 'stem',
        confidence: 0.92,
      };
    }
    var prefixHit = prefixCorrection(token);
    if (prefixHit) return prefixHit;
    var fuzzyHit = fuzzyCorrection(token);
    if (fuzzyHit) return fuzzyHit;
    return { term: token, method: 'unchanged', confidence: 0 };
  }

  function correctTokens(tokens) {
    var corrections = [];
    var corrected = tokens.map(function (tok) {
      var result = correctToken(tok);
      if (result.term !== tok) {
        corrections.push({ from: tok, to: result.term, method: result.method });
      }
      return result.term;
    });
    return { tokens: corrected, corrections: corrections };
  }

  /* ------------------------------------------------------------------ */
  /* Synonyms + intent                                                   */
  /* ------------------------------------------------------------------ */

  function expandSynonyms(tokens) {
    var expanded = [];
    var seen = Object.create(null);
    tokens.forEach(function (tok) {
      var canonical = indexes.synonymToCanonical[tok] || tok;
      if (!seen[canonical]) {
        seen[canonical] = true;
        expanded.push(canonical);
      }
    });
    return expanded;
  }

  function detectBrand(tokens) {
    var joined = tokens.join(' ');
    var found = null;
    Object.keys(indexes.brandSet).forEach(function (brand) {
      if (!brand) return;
      if (tokens.indexOf(brand) !== -1 || joined.indexOf(brand) !== -1) {
        if (!found || brand.length > found.length) found = indexes.brandSet[brand] || brand;
      }
    });
    return found;
  }

  function extractIntent(tokens) {
    var intent = {
      gender: null,
      category: null,
      categoryFamily: null,
      color: null,
      brand: null,
      style: [],
      size: null,
      material: null,
      occasion: null,
    };
    tokens.forEach(function (tok) {
      if (!intent.gender && indexes.genderLookup[tok]) intent.gender = indexes.genderLookup[tok];
      if (!intent.color && indexes.colorSet[tok]) intent.color = indexes.colorSet[tok];
      if (!intent.category && indexes.categorySet[tok]) intent.category = indexes.categorySet[tok];
      if (indexes.styleSet[tok] && intent.style.indexOf(tok) === -1) intent.style.push(tok);
      if (!intent.size && indexes.sizeSet[tok]) intent.size = tok;
      if (!intent.material && indexes.materialSet[tok]) intent.material = tok;
      if (!intent.occasion && indexes.occasionSet[tok]) intent.occasion = tok;
    });
    intent.brand = detectBrand(tokens);
    if (intent.category) intent.categoryFamily = indexes.familyOf[intent.category] || intent.category;
    return intent;
  }

  /* ------------------------------------------------------------------ */
  /* Shopify query construction + fallbacks                              */
  /* ------------------------------------------------------------------ */

  function dropStopwords(tokens) {
    return tokens.filter(function (tok) {
      return !indexes.stopSet[tok];
    });
  }

  function phraseForShopify(tokens) {
    return dropStopwords(tokens)
      .map(function (tok) {
        if (tok === 'tshirt') return 't-shirt';
        if (tok === 'handbag') return 'handbag';
        if (tok === 'coord') return 'co-ord';
        if (tok === 'newbalance') return 'new balance';
        if (tok === 'airforce') return 'air force';
        if (tok === 'hm') return 'h&m';
        return tok;
      })
      .join(' ')
      .trim();
  }

  function uniqueQueries(list) {
    var seen = Object.create(null);
    var out = [];
    list.forEach(function (q) {
      var n = String(q || '').replace(/\s+/g, ' ').trim();
      if (!n || seen[n]) return;
      seen[n] = true;
      out.push(n);
    });
    return out;
  }

  function buildFallbackQueries(tokens, intent, shopifyQuery) {
    var queries = [];
    var brand = intent.brand;
    var category = intent.category;
    var family = intent.categoryFamily;
    var color = intent.color;
    var gender = intent.gender;
    var style = (intent.style || [])[0];

    queries.push(shopifyQuery);

    var attr = [brand, color, style, category, gender].filter(Boolean).join(' ');
    if (attr) queries.push(attr);

    if (brand && category && color) queries.push([brand, color, category].join(' '));
    if (brand && category) queries.push(brand + ' ' + category);
    if (brand && family && family !== category) queries.push(brand + ' ' + family);
    if (color && category) queries.push(color + ' ' + category);
    if (category && gender) queries.push(gender + ' ' + category);
    if (style && category) queries.push(style + ' ' + category);
    if (brand) queries.push(brand);
    if (category) queries.push(category);
    else if (family) queries.push(family);

    return uniqueQueries(queries).filter(function (q) {
      return q !== shopifyQuery;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Process pipeline                                                    */
  /* ------------------------------------------------------------------ */

  var memoryCache = Object.create(null);

  function cacheGet(key) {
    var hit = memoryCache[key];
    if (!hit) return null;
    if (Date.now() - hit.at > config.CACHE_TTL_MS) {
      delete memoryCache[key];
      return null;
    }
    return hit.value;
  }

  function cacheSet(key, value) {
    var keys = Object.keys(memoryCache);
    if (keys.length >= config.CACHE_MAX_ENTRIES) {
      keys
        .sort(function (a, b) {
          return memoryCache[a].at - memoryCache[b].at;
        })
        .slice(0, Math.ceil(keys.length / 4))
        .forEach(function (k) {
          delete memoryCache[k];
        });
    }
    memoryCache[key] = { at: Date.now(), value: value };
    return value;
  }

  function process(query, options) {
    options = options || {};
    var original = String(query || '');
    var cacheKey = original.toLowerCase().replace(/\s+/g, ' ').trim();
    if (!options.skipCache && cacheKey) {
      var cached = cacheGet('process:' + cacheKey);
      if (cached) return cached;
    }

    var originalLight = String(original)
      .toLowerCase()
      .replace(/[’']/g, '')
      .replace(/[-_]/g, ' ')
      .replace(/[^a-z0-9\s]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
    var compactSource = originalLight.replace(/\s+/g, '');
    var compactHit = null;
    if (
      !indexes.exact[originalLight] &&
      !indexes.synonymToCanonical[originalLight] &&
      !indexes.exact[compactSource]
    ) {
      compactHit = resolveCompactPhrase(compactSource);
    }
    var normalized = normalizeQuery(original);
    var corrected = correctTokens(normalized.tokens);
    var expanded = expandSynonyms(corrected.tokens);
    var intent = extractIntent(expanded);
    var rewritten = phraseForShopify(expanded) || normalized.normalized || originalLight;
    var hasUsefulCorrection = (corrected.corrections || []).length > 0;
    var compounded = !!(normalized.normalized && normalized.normalized !== originalLight);
    var shopifyQuery = originalLight;
    if (compactHit) shopifyQuery = compactHit;
    else if (hasUsefulCorrection || compounded) shopifyQuery = rewritten;
    var nearest = nearestMatches(originalLight, { limit: config.SIMILARITY_MAX_MATCHES || 6 });
    var similarQuery = similarQueryFor(originalLight);
    var retrievalQuery = original;
    if (similarQuery && normalizeToken(similarQuery) !== originalLight) {
      retrievalQuery = similarQuery;
    } else if (
      shopifyQuery &&
      shopifyQuery !== originalLight &&
      (hasUsefulCorrection || compactHit)
    ) {
      retrievalQuery = shopifyQuery;
    }
    var fallbackQueries = uniqueQueries(
      [similarQuery, originalLight, rewritten, compactHit].concat(buildFallbackQueries(expanded, intent, shopifyQuery))
    ).filter(function (q) {
      return q && q !== shopifyQuery;
    });

    var result = {
      original: original,
      normalized: normalized.normalized,
      tokens: expanded,
      corrections: corrected.corrections,
      correctedQuery: corrected.tokens.join(' '),
      intent: intent,
      shopifyQuery: shopifyQuery,
      similarQuery: similarQuery || '',
      retrievalQuery: retrievalQuery,
      nearestMatches: nearest,
      fallbackQueries: fallbackQueries,
      usedExpensivePath: corrected.corrections.some(function (c) {
        return c.method === 'fuzzy';
      }),
    };

    if (!options.skipCache && cacheKey) cacheSet('process:' + cacheKey, result);
    return result;
  }

  /* ------------------------------------------------------------------ */
  /* Relevance scoring + ranking                                         */
  /* ------------------------------------------------------------------ */

  function haystackOf(product) {
    return normalizeToken(
      [
        product.title,
        product.vendor,
        product.type,
        product.tags,
        product.description,
        product.collections,
      ]
        .filter(Boolean)
        .join(' ')
    );
  }

  function fieldScore(text, query, weight) {
    var t = normalizeToken(text);
    var q = normalizeToken(query);
    if (!t || !q) return 0;
    if (t === q) return weight;
    if (t.indexOf(q) === 0) return Math.round(weight * 0.9);
    if (t.indexOf(' ' + q) !== -1) return Math.round(weight * 0.82);
    if (t.indexOf(q) !== -1) return Math.round(weight * 0.7);
    var tokens = q.split(/\s+/).filter(Boolean);
    if (!tokens.length) return 0;
    var hits = 0;
    tokens.forEach(function (tok) {
      if (t.indexOf(tok) !== -1) hits++;
    });
    if (!hits) return 0;
    return Math.round(weight * 0.18 * hits);
  }

  function detectProductGender(product, haystack) {
    if (product.gender === 'men' || product.gender === 'women' || product.gender === 'kids') {
      return product.gender;
    }
    if (/\b(women|womens|woman|ladies|lady|female|girls|girl)\b/.test(haystack)) return 'women';
    if (/\b(men|mens|man|male|gents|boys|boy)\b/.test(haystack)) return 'men';
    if (/\b(kids|kid|children|child)\b/.test(haystack)) return 'kids';
    return null;
  }

  function detectProductColor(product, haystack) {
    var found = null;
    Object.keys(indexes.colorSet).forEach(function (color) {
      if (haystack.indexOf(color) !== -1) {
        if (!found || color.length > found.length) found = color;
      }
    });
    return found;
  }

  function detectProductCategory(product, haystack) {
    var type = normalizeToken(product.type || '');
    if (type && indexes.categorySet[lightStem(type)]) return lightStem(type);
    var found = null;
    Object.keys(indexes.categorySet).forEach(function (cat) {
      if (haystack.indexOf(cat) !== -1) {
        if (!found || cat.length > found.length) found = cat;
      }
    });
    return found;
  }

  function sameFamily(a, b) {
    if (!a || !b) return false;
    if (a === b) return true;
    return (indexes.familyOf[a] || a) === (indexes.familyOf[b] || b);
  }

  function scoreProduct(product, parsed) {
    product = product || {};
    parsed = parsed || process('');
    var intent = parsed.intent || {};
    var query = parsed.shopifyQuery || parsed.correctedQuery || parsed.normalized || '';
    var title = product.title || '';
    var vendor = product.vendor || '';
    var type = product.type || '';
    var tags = Array.isArray(product.tags) ? product.tags.join(' ') : product.tags || '';
    var haystack = haystackOf(product);
    var relevance = 0;
    var reasons = [];

    var nameSim = productNameSimilarity(product, parsed);
    var titleExact = fieldScore(title, query, config.TITLE_MATCH_WEIGHT);
    var titleNear =
      nameSim.title >= (config.SIMILARITY_MIN || 0.58)
        ? Math.round(config.TITLE_MATCH_WEIGHT * nameSim.title * 0.9)
        : 0;
    var titleHit = titleExact > titleNear ? titleExact : titleNear;
    if (titleHit) {
      relevance += titleHit;
      reasons.push(titleNear > titleExact ? 'title-similar' : 'title');
    }

    if (intent.brand) {
      var vendorN = normalizeToken(vendor);
      var brandN = normalizeToken(intent.brand);
      if (vendorN === brandN || vendorN.indexOf(brandN) !== -1 || haystack.indexOf(brandN) !== -1) {
        relevance += config.BRAND_MATCH_WEIGHT;
        reasons.push('brand');
      } else if (nameSim.brand >= (config.SIMILARITY_STRONG || 0.78)) {
        relevance += Math.round(config.BRAND_MATCH_WEIGHT * nameSim.brand);
        reasons.push('brand-similar');
      } else {
        relevance -= config.BRAND_MISMATCH_PENALTY;
        reasons.push('brand-mismatch');
      }
    } else {
      var vendorExact = fieldScore(vendor, query, Math.round(config.BRAND_MATCH_WEIGHT * 0.55));
      var vendorNear =
        nameSim.brand >= (config.SIMILARITY_MIN || 0.58)
          ? Math.round(config.BRAND_MATCH_WEIGHT * nameSim.brand * 0.85)
          : 0;
      var vendorHit = vendorExact > vendorNear ? vendorExact : vendorNear;
      if (vendorHit) {
        relevance += vendorHit;
        reasons.push(vendorNear > vendorExact ? 'brand-similar' : 'brand');
      }
    }

    var productCategory = detectProductCategory(product, haystack);
    if (intent.category) {
      if (productCategory && sameFamily(productCategory, intent.category)) {
        relevance += config.CATEGORY_MATCH_WEIGHT;
        reasons.push('category');
      } else if (haystack.indexOf(intent.category) !== -1 || normalizeToken(type).indexOf(intent.category) !== -1) {
        relevance += config.PRODUCT_TYPE_MATCH_WEIGHT;
        reasons.push('type');
      } else {
        relevance -= config.CATEGORY_MISMATCH_PENALTY;
        reasons.push('category-mismatch');
      }
    } else {
      relevance += fieldScore(type, query, config.PRODUCT_TYPE_MATCH_WEIGHT * 0.5);
    }

    if (intent.color) {
      var productColor = detectProductColor(product, haystack);
      if (productColor === intent.color || haystack.indexOf(intent.color) !== -1) {
        relevance += config.COLOR_MATCH_WEIGHT;
        reasons.push('color');
      } else if (productColor && productColor !== intent.color) {
        relevance -= config.COLOR_MISMATCH_PENALTY;
        reasons.push('color-mismatch');
      }
    }

    if (intent.gender) {
      var productGender = detectProductGender(product, haystack);
      if (productGender === intent.gender) {
        relevance += config.GENDER_MATCH_WEIGHT;
        reasons.push('gender');
      } else if (productGender && productGender !== intent.gender) {
        relevance -= config.GENDER_MISMATCH_PENALTY;
        reasons.push('gender-mismatch');
      }
    }

    (intent.style || []).forEach(function (style) {
      if (haystack.indexOf(style) !== -1) {
        relevance += config.STYLE_MATCH_WEIGHT;
        reasons.push('style');
      }
    });

    if (intent.material && haystack.indexOf(intent.material) !== -1) {
      relevance += config.ATTRIBUTE_MATCH_WEIGHT;
      reasons.push('material');
    }

    var tagScore = fieldScore(tags, query, config.TAG_MATCH_WEIGHT);
    if (tagScore) {
      relevance += tagScore;
      reasons.push('tags');
    }

    (parsed.corrections || []).forEach(function (c) {
      if (c.to && haystack.indexOf(c.to) !== -1) {
        relevance += config.SYNONYM_MATCH_WEIGHT;
        reasons.push('corrected-term');
      }
    });

    (parsed.tokens || []).forEach(function (tok) {
      if (indexes.stopSet[tok]) return;
      if (haystack.indexOf(tok) !== -1) {
        relevance += config.PARTIAL_MATCH_WEIGHT;
      } else if (tok.length >= 4) {
        var fuzzyHit = false;
        haystack.split(/\s+/).some(function (word) {
          if (word.length < 4) return false;
          if (damerauLevenshtein(tok, word) === 1) {
            fuzzyHit = true;
            return true;
          }
          return false;
        });
        if (fuzzyHit) relevance += config.FUZZY_MATCH_WEIGHT;
      }
    });

    if (product.available === false) {
      /* sold-out stays in the list but ranks lower */
    } else {
      relevance += config.AVAILABILITY_WEIGHT;
    }

    return {
      relevance: Math.max(0, relevance),
      reasons: reasons,
      category: productCategory,
      gender: detectProductGender(product, haystack),
    };
  }

  function applyBoostGuardrail(relevance, boostRaw) {
    if (!boostRaw) return 0;
    if (relevance < config.MIN_RELEVANCE_FOR_BOOST) return 0;
    return boostRaw;
  }

  function rankProducts(products, parsed, options) {
    options = options || {};
    parsed = parsed || process('');
    var maxKeep = options.max != null ? options.max : 0;
    var scored = (products || []).map(function (product, index) {
      var scoredProduct = scoreProduct(product, parsed);
      var boostRaw = 0;
      if (product.boosted) {
        var position = product.boostIndex != null ? product.boostIndex : 0;
        boostRaw = Math.max(2, config.BOOST_WEIGHT - position * 2);
      } else if (product.shopifyIndex != null && product.shopifyIndex < 8) {
        boostRaw = Math.max(0, (8 - product.shopifyIndex) * (config.SHOPIFY_POSITION_WEIGHT / 4));
      }
      var boost = applyBoostGuardrail(scoredProduct.relevance, boostRaw);
      var finalScore = scoredProduct.relevance + boost;
      return assign({}, product, {
        relevance: scoredProduct.relevance,
        boostScore: boost,
        finalScore: finalScore,
        reasons: scoredProduct.reasons,
        shopifyIndex: product.shopifyIndex != null ? product.shopifyIndex : index,
      });
    });

    scored.sort(function (a, b) {
      if (b.finalScore !== a.finalScore) return b.finalScore - a.finalScore;
      return a.shopifyIndex - b.shopifyIndex;
    });

    var keepMin = config.MIN_RELEVANCE_TO_KEEP;
    var nameMin = config.SIMILARITY_MIN || 0.58;
    scored = scored.filter(function (p) {
      if (p.relevance >= config.STRONG_MATCH_THRESHOLD) return true;
      if (p.relevance < keepMin) return false;
      return productNameSimilarity(p, parsed).best >= nameMin;
    });

    if (maxKeep) scored = scored.slice(0, maxKeep);
    return scored;
  }

  /* ------------------------------------------------------------------ */
  /* Suggestions                                                         */
  /* ------------------------------------------------------------------ */

  function titleCase(value) {
    return String(value || '')
      .split(/\s+/)
      .map(function (part) {
        if (!part) return '';
        if (part === 't-shirt' || part === 'tshirt') return 'T-Shirts';
        return part.charAt(0).toUpperCase() + part.slice(1);
      })
      .join(' ');
  }

  function suggest(query, limit) {
    var parsed = process(query);
    var intent = parsed.intent || {};
    var suggestions = [];
    var seen = Object.create(null);

    function add(label) {
      var key = String(label || '').toLowerCase();
      if (!key || seen[key]) return;
      seen[key] = true;
      suggestions.push(label);
    }

    if (intent.brand) add(titleCase(intent.brand));
    if (intent.brand && intent.category) add(titleCase(intent.brand + ' ' + intent.category));
    if (intent.color && intent.category) add(titleCase(intent.color + ' ' + intent.category));
    if (intent.gender && intent.category) add(titleCase(intent.gender + ' ' + intent.category));
    if (intent.category) add(titleCase(intent.category));
    if (parsed.shopifyQuery) add(titleCase(parsed.shopifyQuery));

    (catalog.collections || []).forEach(function (title) {
      var n = normalizeToken(title);
      if (!n) return;
      if ((parsed.tokens || []).every(function (tok) {
        return !tok || indexes.stopSet[tok] || n.indexOf(tok) !== -1;
      })) {
        add(title);
      }
    });

    return suggestions.slice(0, limit || 6);
  }

  /* ------------------------------------------------------------------ */
  /* Analytics                                                           */
  /* ------------------------------------------------------------------ */

  var analyticsState = {
    events: [],
    lastSearch: null,
  };

  function analyticsPush(event) {
    analyticsState.events.push(event);
    if (analyticsState.events.length > config.ANALYTICS_MAX_EVENTS) {
      analyticsState.events = analyticsState.events.slice(-config.ANALYTICS_MAX_EVENTS);
    }
    persistAnalytics();
    var globalRef = typeof globalThis !== 'undefined' ? globalThis : null;
    if (globalRef && globalRef.dataLayer && typeof globalRef.dataLayer.push === 'function') {
      try {
        globalRef.dataLayer.push(assign({ event: 'skipp_search' }, event));
      } catch (e) {}
    }
  }

  function persistAnalytics() {
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(
        'skipp_search_analytics',
        JSON.stringify({
          events: analyticsState.events.slice(-config.ANALYTICS_MAX_EVENTS),
          learned: learnedTypos,
        })
      );
    } catch (e) {}
  }

  function loadAnalytics() {
    try {
      if (typeof localStorage === 'undefined') return;
      var raw = localStorage.getItem('skipp_search_analytics');
      if (!raw) return;
      var parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.events)) analyticsState.events = parsed.events;
      if (parsed && parsed.learned) learnedTypos = parsed.learned;
    } catch (e) {}
  }

  loadAnalytics();

  function trackSearch(parsed, extras) {
    extras = extras || {};
    var event = {
      type: 'search',
      original: parsed.original,
      normalized: parsed.normalized,
      corrected: parsed.shopifyQuery,
      corrections: parsed.corrections || [],
      resultCount: extras.resultCount != null ? extras.resultCount : null,
      timestamp: Date.now(),
    };
    analyticsState.lastSearch = event;
    analyticsPush(event);
    return event;
  }

  function trackZeroResult(parsed) {
    return trackSearch(parsed, { resultCount: 0 });
  }

  function trackProductSignal(type, product) {
    var last = analyticsState.lastSearch;
    if (!last) return null;
    var event = {
      type: type,
      original: last.original,
      corrected: last.corrected,
      productId: product && (product.id || product.productId),
      timestamp: Date.now(),
    };
    if (type === 'click' && last.corrections && last.corrections.length) {
      last.corrections.forEach(function (c) {
        if (c.from && c.to) learnedTypos[c.from] = c.to;
      });
    }
    analyticsPush(event);
    return event;
  }

  function getQualityMetrics() {
    var searches = analyticsState.events.filter(function (e) {
      return e.type === 'search';
    });
    var clicks = analyticsState.events.filter(function (e) {
      return e.type === 'click';
    });
    var carts = analyticsState.events.filter(function (e) {
      return e.type === 'add_to_cart';
    });
    var purchases = analyticsState.events.filter(function (e) {
      return e.type === 'purchase';
    });
    var zero = searches.filter(function (e) {
      return e.resultCount === 0;
    });
    var low = searches.filter(function (e) {
      return e.resultCount != null && e.resultCount > 0 && e.resultCount < 4;
    });
    var typos = searches.filter(function (e) {
      return e.corrections && e.corrections.length;
    });
    return {
      searches: searches.length,
      zeroResultRate: searches.length ? zero.length / searches.length : 0,
      lowResultRate: searches.length ? low.length / searches.length : 0,
      searchToClickRate: searches.length ? clicks.length / searches.length : 0,
      searchToCartRate: searches.length ? carts.length / searches.length : 0,
      searchToPurchaseRate: searches.length ? purchases.length / searches.length : 0,
      typoQueryRate: searches.length ? typos.length / searches.length : 0,
      frequentTypos: learnedTypos,
      zeroResultQueries: zero.map(function (e) {
        return e.original;
      }),
    };
  }

  /* ------------------------------------------------------------------ */
  /* Public API                                                          */
  /* ------------------------------------------------------------------ */

  return {
    config: config,
    setCatalog: setCatalog,
    mergeCatalog: mergeCatalog,
    getCatalog: function () {
      return catalog;
    },
    normalizeQuery: normalizeQuery,
    tokenize: tokenize,
    damerauLevenshtein: damerauLevenshtein,
    jaroWinkler: jaroWinkler,
    correctToken: correctToken,
    correctTokens: correctTokens,
    expandSynonyms: expandSynonyms,
    extractIntent: extractIntent,
    process: process,
    similarityScore: similarityScore,
    nearestMatches: nearestMatches,
    similarQueryFor: similarQueryFor,
    productNameSimilarity: productNameSimilarity,
    scoreProduct: scoreProduct,
    rankProducts: rankProducts,
    applyBoostGuardrail: applyBoostGuardrail,
    suggest: suggest,
    buildFallbackQueries: function (query) {
      var parsed = typeof query === 'string' ? process(query) : query;
      return parsed.fallbackQueries || [];
    },
    setLearnedTypos: setLearnedTypos,
    trackSearch: trackSearch,
    trackZeroResult: trackZeroResult,
    trackProductClick: function (product) {
      return trackProductSignal('click', product);
    },
    trackAddToCart: function (product) {
      return trackProductSignal('add_to_cart', product);
    },
    trackPurchase: function (product) {
      return trackProductSignal('purchase', product);
    },
    getQualityMetrics: getQualityMetrics,
    cacheGet: cacheGet,
    cacheSet: cacheSet,
    _rebuildIndexes: rebuildIndexes,
  };
});
